/**
 * <categories-explorer> - enhances the server-rendered /collections directory.
 *
 * No-JS state = every parent pane stacked as a plain link directory. On upgrade:
 * - Desktop (>=750px): sticky rail + one visible pane; a rail row swaps panes on
 *   click/Enter ONLY (never hover) and sets aria-current.
 * - Mobile (<750px): two-level drill (parent list -> pane), CSS-driven slide.
 * location.hash mirrors the active pane via replaceState (no scroll, no history
 * spam) and deep-links a pane on load. Crossing the breakpoint resets mobile to
 * the parent list. All animation is CSS keyed off [data-view].
 *
 * Markup contract: sections/main-categories-explorer.liquid.
 */
(() => {
  if (customElements.get('categories-explorer')) return;

  class CategoriesExplorer extends HTMLElement {
    connectedCallback() {
      this.listeners = new AbortController();
      const { signal } = this.listeners;

      this.railLinks = Array.from(this.querySelectorAll('.categories-explorer__rail-link'));
      this.panes = Array.from(this.querySelectorAll('.categories-explorer__pane'));
      if (!this.railLinks.length || !this.panes.length) return;

      this.desktopMQ = window.matchMedia('(min-width: 750px)');
      this.returnFocusLink = null;

      const hashIndex = this.indexFromHash();
      this.initialDeepLink = hashIndex >= 0;
      this.activeIndex = this.initialDeepLink ? hashIndex : 0;

      this.railLinks.forEach((link, index) => {
        link.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            this.activate(index, link);
          },
          { signal }
        );
      });

      this.querySelectorAll('.categories-explorer__back').forEach((button) => {
        button.addEventListener('click', () => this.drillOut(), { signal });
      });

      this.desktopMQ.addEventListener('change', () => this.applyMode(), { signal });

      this.setAttribute('data-enhanced', '');
      this.applyMode();
    }

    disconnectedCallback() {
      this.listeners.abort();
    }

    indexFromHash() {
      const id = window.location.hash.slice(1);
      if (!id) return -1;
      return this.panes.findIndex((pane) => pane.id === id);
    }

    select(index, updateHash = true) {
      this.railLinks.forEach((link, i) => {
        if (i === index) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
      this.panes.forEach((pane, i) => {
        pane.hidden = i !== index;
      });
      this.activeIndex = index;
      if (updateHash) {
        window.history.replaceState(null, '', '#' + this.panes[index].id);
      }
    }

    activate(index, triggerLink) {
      if (this.desktopMQ.matches) this.select(index);
      else this.drillIn(index, triggerLink);
    }

    drillIn(index, triggerLink) {
      this.select(index);
      this.returnFocusLink = triggerLink;
      this.setAttribute('data-view', 'pane');
      const back = this.panes[index].querySelector('.categories-explorer__back');
      if (back) back.focus({ preventScroll: true });
    }

    drillOut() {
      this.setAttribute('data-view', 'index');
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      // Deep-linked loads drill in without a tapped row: fall back to the active one.
      const target = this.returnFocusLink || this.railLinks[this.activeIndex];
      if (target) target.focus({ preventScroll: true });
    }

    // Desktop shows the active pane in place; mobile shows the parent list unless
    // the load was deep-linked (drill straight in, once). Consume the deep-link
    // flag on the first call so a later breakpoint cross resets mobile to index.
    applyMode() {
      const deepLink = this.initialDeepLink;
      this.initialDeepLink = false;

      if (this.desktopMQ.matches) {
        this.removeAttribute('data-view');
        this.select(this.activeIndex, false);
      } else if (deepLink) {
        this.select(this.activeIndex, false);
        this.setAttribute('data-view', 'pane');
      } else {
        this.removeAttribute('data-view');
      }
    }
  }

  customElements.define('categories-explorer', CategoriesExplorer);
})();
